// apps/hr/src/pages/hr/Promotions.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  XCircle,
  Plus,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPromotions();
  }, [filter]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      let filterQuery = '';
      if (filter === 'pending') {
        filterQuery = 'status = "pending"';
      } else if (filter === 'approved') {
        filterQuery = 'status = "approved"';
      } else if (filter === 'rejected') {
        filterQuery = 'status = "rejected"';
      }
      const records = await pb.collection('promotions').getFullList({
        filter: filterQuery,
        sort: '-created',
        expand: 'employee',
      });
      setPromotions(records);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      await pb.collection('promotions').update(id, { status });
      toast.success(`Promotion ${status} successfully`);
      fetchPromotions();
    } catch (error) {
      console.error('Error updating promotion:', error);
      toast.error('Failed to update promotion');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-slate-100 text-slate-800'}`}>
        {status || 'Pending'}
      </span>
    );
  };

  const filteredPromotions = promotions.filter((p) => {
    const name = p.expand?.employee?.name || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Promotions</h1>
          <p className="text-sm text-slate-500">Manage employee promotions</p>
        </div>
        <Link
          to="/promotions/new"
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition flex items-center gap-2 shadow-sm"
        >
          <Plus className="w-4 h-4" /> New Promotion
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by employee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                  filter === status ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : filteredPromotions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No promotions</h3>
          <p className="text-slate-500">No promotion records found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPromotions.map((promotion) => (
            <motion.div
              key={promotion.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold">
                    {promotion.expand?.employee?.name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">
                      {promotion.expand?.employee?.name || 'Unknown'}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {promotion.fromDesignation} → {promotion.toDesignation}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(promotion.effectiveDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(promotion.status)}
                  {promotion.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAction(promotion.id, 'approved')}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleAction(promotion.id, 'rejected')}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {promotion.reason && (
                <p className="mt-2 text-sm text-slate-600 bg-slate-50 p-2 rounded">
                  Reason: {promotion.reason}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Promotions;